import { createNameTag } from './NameTag';
import { getFloatingLabelPool, type FloatingLabelStyle } from './FloatingLabel';
import type { INameTag } from '../IRenderer';
import { Tween, Easing } from '../Tween';
import { Z_LAYERS } from '../Z_LAYERS';
import { FCT_STYLE } from '../../../config/ui/fctStyle.config';
import type { FctMotionVerb } from '../../../config/ui/fctStyle.config';
import { FCT_VERBS } from './fctMotionVerbs';
import { prefersReducedMotion } from '../../systems/accessibility/motionPrefs';

/**
 * spawnFloatingDamageText — substrate-agnostic floating damage number.
 *
 * Two motion paths (FCT choreography spec Phase 1):
 *
 *  - LEGACY (no `verb`): the original rise-by-`riseY` + power2 fade tween,
 *    byte-identical to the pre-grammar motion. Every non-migrated caller
 *    (MobRenderer local mobs, NPCRenderer, dev actions) stays on this path
 *    so default numbers never shift.
 *
 *  - VERB (`verb` set): a named motion program from fctMotionVerbs drives
 *    dx/dy/alpha/scale via the rAF loop; the executor additionally owns
 *      · per-target lateral spawn slots (spec §3.1 — kills overprint),
 *      · the hard concurrency cap + density-triggered degradation (§3.2/3.3),
 *      · setScale wiring (INameTag.setScale is real — IRenderer.ts:100),
 *      · reduced-motion downgrade to the legacy plain rise-fade.
 *
 * Drives the animation via requestAnimationFrame + the Tween primitive,
 * so it works identically under ?renderer=phaser and ?renderer=pixi —
 * no scene.tweens.add coupling. The label is a pooled atlas SpriteLabel
 * (FloatingLabel slot; plan 16 §2.3): no Text created or destroyed per number.
 *
 * Phase H-B Stage 5j; verb engine 2026-07-17.
 */

export interface FloatingDamageOptions {
  x: number;
  y: number;
  text: string;
  /** Hex int (e.g. 0xff0000). */
  color: number;
  /** Pixel font size, applied via NameTagStyle. */
  fontSize: number;
  /** How far the text rises before fade-out (legacy path). Default 60px. */
  riseY?: number;
  /** Total animation duration. Default 1200ms. */
  durationMs?: number;
  /** Named motion program. Undefined ⇒ legacy rise-fade (byte-parity path). */
  verb?: FctMotionVerb;
  /** Verb randomness seed. Defaults to the allocated slot offset so arc drift follows slot sign. */
  seed?: number;
  /** Weight tier 0–3 — feeds the cap/degradation gates. */
  tier?: number;
  /** Per-target slot-ring key (usually the target entity id). */
  targetKey?: string;
  /** Explicit lateral offset — companions reuse their number's slot instead of allocating. */
  slotDx?: number;
  /** Never dropped by cap/degrade gates (crits, local-player numbers, special beats). */
  alwaysRender?: boolean;
  /** Companion label riding another spawn: no slot alloc, no cap/degrade accounting. */
  companion?: boolean;
  /**
   * Verb path: a moving origin. The number keeps its spawn offset from this
   * point every frame (a position write, nothing redrawn) — the local
   * player's own hit numbers ride with the body through a knockback. A null
   * reading holds the last offset. The reduced-motion legacy path ignores it.
   */
  follow?: () => { x: number; y: number } | null;
}

export interface FloatingDamageResult {
  /** False when the cap/degradation gate dropped the spawn. */
  spawned: boolean;
  /** Lateral slot offset applied (0 on the legacy path) — reuse for companions. */
  slotDx: number;
}

/** A pooled atlas label when the Pixi boot wired one, else a plain name tag. */
function acquireLabel(scene: unknown, x: number, y: number, text: string, style: FloatingLabelStyle): INameTag {
  const pool = getFloatingLabelPool();
  return pool ? pool.acquire(x, y, text, style) : createNameTag(scene, x, y, text, style);
}

function releaseLabel(label: INameTag): void {
  const pool = getFloatingLabelPool();
  if (pool) pool.release(label);
  else label.destroy();
}

// ─── Executor state (module-level, verb path) ────────────────────────────

let liveCount = 0;
/** Trailing spawn-attempt timestamps for the density gate (verb path only). */
const spawnTimes: number[] = [];
const slotRings = new Map<string, { idx: number; lastAt: number }>();

function bumpQaCounter(name: 'capDropCount' | 'degradeDropCount'): void {
  if (typeof window === 'undefined') return;
  const dbg = (window as any).__damagePresentation;
  if (dbg) dbg[name] = (dbg[name] ?? 0) + 1;
}

function allocateSlot(targetKey: string, now: number): number {
  const slots = FCT_STYLE.slotOffsetsX;
  const ring = slotRings.get(targetKey);
  const idx = ring && now - ring.lastAt <= FCT_STYLE.slotMemoryMs
    ? (ring.idx + 1) % slots.length
    : 0;
  slotRings.set(targetKey, { idx, lastAt: now });
  // Opportunistic prune so idle targets don't accumulate forever.
  if (slotRings.size > 128) {
    for (const [k, r] of slotRings) {
      if (now - r.lastAt > FCT_STYLE.slotMemoryMs * 4) slotRings.delete(k);
    }
  }
  return slots[idx];
}

/** Spawns in the trailing second, counting this attempt. */
function recordSpawnAttempt(now: number): number {
  spawnTimes.push(now);
  while (spawnTimes.length > 0 && now - spawnTimes[0] > 1000) spawnTimes.shift();
  return spawnTimes.length;
}

/** Test seam — reset slots, density window, and live count. */
export function __resetFloatingDamageStateForTests(): void {
  liveCount = 0;
  spawnTimes.length = 0;
  slotRings.clear();
}

/** QA observability — current live-label count. */
export function getFloatingDamageLiveCount(): number {
  return liveCount;
}

// ─── Spawn ───────────────────────────────────────────────────────────────

export function spawnFloatingDamageText(scene: unknown, opts: FloatingDamageOptions): FloatingDamageResult {
  const { verb } = opts;

  // Reduced-motion: any styled verb degrades to the plain legacy rise-fade
  // (spec §3.4). Style (text/size/color) is kept; motion is not.
  if (verb !== undefined && !prefersReducedMotion()) {
    return spawnVerb(scene, opts, verb);
  }
  return spawnLegacy(scene, opts);
}

/** Legacy motion — byte-identical to the pre-grammar rise-fade. */
function spawnLegacy(scene: unknown, opts: FloatingDamageOptions): FloatingDamageResult {
  const {
    x, y, text, color, fontSize,
    riseY = 60,
    durationMs = 1200,
  } = opts;

  const label = acquireLabel(scene, x, y, text, {
    fontSize,
    color,
    depth: Z_LAYERS.FLOATING_DAMAGE_TEXT,
  });
  liveCount++;

  // Rise + fade tween state (rAF-driven so it works substrate-agnostically).
  const state = { y, alpha: 1 };
  const startY = y;
  const riseTween = new Tween(state, {
    to: { y: startY - riseY, alpha: 0 },
    durationMs,
    ease: Easing.power2,
    onComplete: () => { releaseLabel(label); liveCount = Math.max(0, liveCount - 1); },
  });

  let lastT = performance.now();
  const tick = () => {
    const now = performance.now();
    const dt = now - lastT;
    lastT = now;

    const alive = riseTween.step(dt);
    label.setPosition(x, state.y);
    label.setAlpha(state.alpha);

    if (alive) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);

  return { spawned: true, slotDx: 0 };
}

/** Verb motion — named program + slots + cap/degradation gates + setScale. */
function spawnVerb(scene: unknown, opts: FloatingDamageOptions, verb: FctMotionVerb): FloatingDamageResult {
  const {
    x, y, text, color, fontSize,
    tier = 1,
    targetKey,
    alwaysRender = false,
    companion = false,
  } = opts;
  let durationMs = opts.durationMs ?? 1200;
  const now = Date.now();

  // Slot allocation — companions reuse their number's offset via slotDx.
  const slotDx = opts.slotDx ?? (targetKey ? allocateSlot(targetKey, now) : 0);
  const seed = opts.seed ?? slotDx;

  let alphaScale = 1;
  if (!companion) {
    // Density-triggered degradation (§3.3): >N spawns in the trailing second
    // drops tier 0 entirely; tier 1 renders dimmer + shorter; 2–3 unchanged.
    const perSec = recordSpawnAttempt(now);
    if (perSec > FCT_STYLE.degradeSpawnsPerSec && !alwaysRender) {
      if (tier <= 0) {
        bumpQaCounter('degradeDropCount');
        return { spawned: false, slotDx };
      }
      if (tier === 1) {
        alphaScale = 0.8;
        durationMs *= 0.8;
      }
    }

    // Hard concurrency cap (§3.2): tier-0/1 non-priority spawns drop at cap;
    // crits, local-player numbers, and special beats always render.
    if (liveCount >= FCT_STYLE.maxConcurrent && !alwaysRender && tier <= 1) {
      bumpQaCounter('capDropCount');
      return { spawned: false, slotDx };
    }
  }

  const program = FCT_VERBS[verb];
  const label = acquireLabel(scene, x + slotDx, y, text, {
    fontSize,
    color,
    depth: Z_LAYERS.FLOATING_DAMAGE_TEXT,
  });
  liveCount++;

  const start = performance.now();
  // Copied: a follow source may hand back the same mutable point every frame.
  const first = opts.follow?.();
  const origin = first ? { x: first.x, y: first.y } : null;
  let ox = 0;
  let oy = 0;
  const tick = () => {
    const t = Math.min((performance.now() - start) / durationMs, 1);
    const frame = program(t, seed);
    const now = origin ? opts.follow!() : null;
    if (origin && now) {
      ox = now.x - origin.x;
      oy = now.y - origin.y;
    }
    label.setPosition(x + ox + slotDx + frame.dx, y + oy + frame.dy);
    label.setAlpha(frame.alpha * alphaScale);
    label.setScale(frame.scale);

    if (t < 1) {
      requestAnimationFrame(tick);
    } else {
      releaseLabel(label);
      liveCount = Math.max(0, liveCount - 1);
    }
  };
  requestAnimationFrame(tick);

  return { spawned: true, slotDx };
}
